<script setup>
import api from "@/api";
import DownloadIcon from "@/assets/icons/download.png";

const steps = [
	"Download the task - a short coding challenge covering the basics of frontend development",
	"Complete it - work on it at your own pace, in your own environment",
	"Fill out the form below - attach your completed task, upload your CV, and answer a few short questions",
	"Submit - we'll review your application and get back to you within 5 business days",
];

const loading = ref(false);

async function downloadTask() {
	loading.value = true;

	try {
		const response = await api.getTask();

		const url = URL.createObjectURL(response.data);

		const link = document.createElement("a");
		link.href = url;
		const disposition = response.headers["content-disposition"];
		const matches = disposition?.match(/filename="(.+)"/);
		const fileName = matches ? matches[1] : "file.pdf";
		link.download = fileName;

		link.click();

		URL.revokeObjectURL(url);
	} catch (error) {
		console.error("ERROR", error);
	} finally {
		loading.value = false;
	}
}
</script>

<template>
	<div class="container">
		<div class="hero-copy">
			<div class="frame-64">
				<h1 class="title">Apply for the Frontend Development Course</h1>
				<p class="subtitle">
					Ready to join? Here's how the application works - it should take
					around 2–3 hours to complete.
				</p>
			</div>
		</div>

		<div class="frame-71">
			<ol class="steps">
				<li v-for="(step, index) in steps" :key="index" class="steps__item">
					{{ step }}
				</li>
			</ol>

			<button class="download-btn" @click="downloadTask">
				<img :src="DownloadIcon" alt="download" />
				Download the task
			</button>
		</div>
	</div>
</template>

<style lang="scss" scoped>
@use "@/assets/css/vars" as *;

.container {
	display: flex;
	max-width: 1100px;
	margin: 0 auto; // dodato
	justify-content: center;
	flex-direction: column;
	align-items: flex-start;
	gap: 24px;
	padding-top: 200px;
	padding-bottom: 200px;
	padding-right: 100px;
	width: 100%;
}

.hero-copy {
	display: flex;
	flex-direction: column;
	gap: 40px;
	width: 100%;
}

.frame-64 {
	display: flex;
	flex-direction: column;
	gap: 24px;
	width: 100%;
}

.title {
	font-family: $font-heading;
	font-weight: $font-bold;
	font-size: $text-hero; // 64px
	line-height: 103%;
	letter-spacing: -2px;
	color: $text-white;
}

.subtitle {
	font-family: $font-heading;
	font-weight: $font-regular;
	font-size: $text-xl; // 24px
	line-height: 26px;
	color: $green-text;
}

.frame-71 {
	display: flex;
	flex-direction: column;
	gap: 19px;
	width: 100%;
}

.steps {
	display: flex;
	flex-direction: column;
	gap: 0; // tekst u Figmi je jedan blok sa line-height 172%, razmak dolazi iz line-height-a
	margin: 0;
	padding-left: 20px;

	&__item {
		font-family: $font-heading;
		font-weight: $font-regular;
		font-size: $text-md; // 18px
		line-height: 172%;
		color: $background-color-f5; // #f5f5f5
	}
}

.download-btn {
	display: inline-flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	padding: 14px 12px;
	gap: 6px;
	width: 216px;
	height: 52px;
	background: $app-color; // #53AA6C
	border-radius: $button-border-radius; // 8px
	text-decoration: none;
	cursor: pointer;
	border: none;
	transition: opacity 0.2s ease;

	&:hover {
		opacity: 0.9;
	}

	&__icon {
		width: 24px;
		height: 24px;
		flex: none;
	}

	font-family: $font-heading;
	font-weight: $font-semibold;
	font-size: 19px;
	line-height: 21px;
	color: $text-white;
}

@media screen and (max-width: 1400px) {
	.container {
		padding: 120px 80px;
	}
}

@media screen and (max-width: 768px) {
	.container {
		gap: 16px;
		// padding: 60px 24px;
		padding: 100px 24px;
	}

	.hero-copy {
		gap: 24px;
	}

	.frame-64 {
		gap: 16px;
	}

	.title {
		font-size: 36px;
		line-height: 110%;
		letter-spacing: -1px;
	}

	.subtitle {
		font-size: $text-base;
		line-height: 140%;
	}

	.frame-71 {
		gap: 16px;
	}

	.steps {
		padding-left: 18px;

		&__item {
			font-size: $text-base;
			line-height: 160%;
		}
	}

	.download-btn {
		width: 100%;
	}
}

@media screen and (max-width: 480px) {
	.container {
		padding: 100px 16px 40px;
	}

	.title {
		font-size: 28px;
	}

	.download-btn {
		font-size: $text-base;
		height: 48px;
	}
}
</style>
